import {
  AddAllUnavailableReason,
  IAddAllAvailability,
  IConfig,
  IPromotion,
  IPromotionShortcut,
  IPromotionsAvailability,
} from '../../interfaces'
import { getPromotionFields } from '../common/getPromotionFields'
import flatTexts from '../flatTexts'

/** Placeholder the add-all reason texts carry for the product list. */
export const ADD_ALL_REASON_PRODUCTS_PLACEHOLDER = '{{products}}'

export const isDisableUnfulfillableAddAllOn = (config?: IConfig): boolean =>
  config?.globals?.promotions?.disableUnfulfillableAddAll === true

/** `globals.promotions.addAllOrNothing`, read as literal `true` only. */
export const isAddAllOrNothingOn = (config?: IConfig): boolean =>
  config?.globals?.promotions?.addAllOrNothing === true

/**
 * A promotion with several buys groups and several gets groups has no single
 * set of lines an add-all button could send, so its button is hidden.
 */
export const hasSeveralBuysAndGetsGroups = (promotion: IPromotion): boolean => {
  const { customerBuysGroups, customerGetsGroups } =
    getPromotionFields(promotion)
  return customerBuysGroups.length > 1 && customerGetsGroups.length > 1
}

interface IFindAddAllAvailabilityArgs {
  availability?: IPromotionsAvailability
  sku?: string
  promotion?: IPromotion | null
  groupIdentifier?: string | null
}

/**
 * The computed availability behind one shortcut, or `undefined` when there is
 * none to apply: no lookup answered yet, it answered for another product, it
 * does not know this promotion, or the shortcut is one group of a multi-group
 * promotion and names no group. `undefined` always means "leave the button as
 * it is".
 */
export const findAddAllAvailability = ({
  availability,
  sku,
  promotion,
  groupIdentifier,
}: IFindAddAllAvailabilityArgs): IAddAllAvailability | undefined => {
  if (!availability || !sku || availability.sku !== sku || !promotion?.id) {
    return undefined
  }
  const entry = availability.promotions[promotion.id]
  if (!entry) return undefined
  const isMultiGroup =
    getPromotionFields(promotion).customerBuysGroups.length > 1
  if (!isMultiGroup) return entry.promotion
  return groupIdentifier ? entry.groups[groupIdentifier] : undefined
}

export type AddAllRefusal = AddAllUnavailableReason | 'severalBuysAndGetsGroups'

/**
 * Why the add-all action must send nothing for this shortcut, or `null` when
 * it may send. Callers check the flag first; this answers only for a
 * storefront that opted in.
 */
export const getAddAllRefusal = (
  args: IFindAddAllAvailabilityArgs,
): AddAllRefusal | null => {
  if (args.promotion && hasSeveralBuysAndGetsGroups(args.promotion)) {
    return 'severalBuysAndGetsGroups'
  }
  const verdict = findAddAllAvailability(args)
  return verdict && !verdict.available ? verdict.reason : null
}

type UnavailableVerdict = Extract<IAddAllAvailability, { available: false }>

const reasonTemplate = (
  reason: AddAllUnavailableReason,
  texts: Record<string, string>,
): string => {
  switch (reason) {
    case 'bundle':
      return texts.addAllChooseOptionsReason || ''
    case 'outOfStock':
      return texts.addAllOutOfStockReason || ''
    default: {
      const unhandled: never = reason
      return unhandled
    }
  }
}

/**
 * The reason line for an unavailable button, from `texts.promotions`. One
 * line, naming every product behind the reason.
 */
export const formatAddAllDisabledReason = (
  { reason, products }: UnavailableVerdict,
  config?: IConfig,
): string => {
  const template = reasonTemplate(reason, flatTexts(config?.texts?.promotions))
  return template
    .split(ADD_ALL_REASON_PRODUCTS_PLACEHOLDER)
    .join(products.join(', '))
}

interface IApplyAddAllAvailabilityArgs {
  availability?: IPromotionsAvailability
  sku: string
  config?: IConfig
}

/**
 * Marks a shortcut's button disabled, with its reason, when the availability
 * says it cannot be fulfilled. A shortcut with no button, or one the
 * availability has nothing to say about, is returned untouched.
 */
export const applyAddAllAvailability = (
  shortcut: IPromotionShortcut,
  { availability, sku, config }: IApplyAddAllAvailabilityArgs,
): IPromotionShortcut => {
  if (!shortcut.showButton) return shortcut
  const groupAvailability = findAddAllAvailability({
    availability,
    sku,
    promotion: shortcut.data.promotion,
    groupIdentifier: shortcut.data.groupIdentifier,
  })
  if (!groupAvailability || groupAvailability.available) return shortcut
  return {
    ...shortcut,
    buttonDisabled: true,
    disabledReason: formatAddAllDisabledReason(groupAvailability, config),
  }
}
